import { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Swords, ShieldCheck, Crown, X, Loader2, Trophy, TrendingUp } from "lucide-react";
import { toast } from "sonner";

import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { onPersonalEvent } from "../hooks/usePersonalSocket";
import { RanksExplainer } from "./Ranks";

import { RankBadge, TIER_META, TIER_ORDER } from "../components/RankBadge";

export function RankedTierChip({ tier, mmr, compact }) {
  const meta = TIER_META[tier] || TIER_META.iron;
  return (
    <span
      data-testid={`ranked-tier-chip-${tier}`}
      className="inline-flex items-center gap-1.5 rounded-full bg-black/30 ring-1 ring-white/10 px-2 py-1"
    >
      <RankBadge tier={tier} size="sm" animated showLabel={false} />
      <span className="text-[10px] uppercase tracking-widest font-bold" style={{ color: meta.primary }}>
        {meta.label}
      </span>
      {!compact && mmr != null ? (
        <span className="text-[10px] text-zinc-200/80">· {mmr}</span>
      ) : null}
    </span>
  );
}

function TierProgress({ mmr, tier }) {
  const meta = TIER_META[tier] || TIER_META.iron;
  const [lo, hi] = meta.band;
  const span = Math.max(1, hi - lo);
  const pct = Math.min(100, Math.max(0, ((mmr - lo) / span) * 100));
  const idx = TIER_ORDER.indexOf(tier);
  const nextKey = TIER_ORDER[idx + 1];
  const next = nextKey ? TIER_META[nextKey] : null;
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-zinc-400">
        <span>{meta.label} · {mmr} MMR</span>
        {next && <span>Next: {next.label} ({next.band[0]})</span>}
      </div>
      <div className="h-2 rounded-full bg-white/5 overflow-hidden">
        <div className="h-full" style={{ width: `${pct}%`, background: `linear-gradient(90deg, ${meta.secondary}, ${meta.primary})` }} />
      </div>
    </div>
  );
}

function ModeCard({ mode, label, sub, block, onPlay, disabled, queued, eligible }) {
  const tier = block?.tier || "iron";
  const mmr = block?.mmr ?? 1000;
  return (
    <div data-testid={`ranked-mode-card-${mode}`}
      className="rounded-2xl border border-white/10 bg-gradient-to-b from-[var(--surface-2)] to-[var(--surface)] p-6 space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.4em] text-[var(--accent)]">{label}</div>
          <h3 className="mt-1 text-2xl font-black">{sub}</h3>
        </div>
        <RankedTierChip tier={tier} mmr={mmr} />
      </div>
      <div className="grid grid-cols-3 gap-2 text-xs">
        <div className="rounded-lg bg-black/40 p-2"><div className="text-[10px] text-zinc-500">Peak MMR</div><div className="font-bold tabular-nums">{block?.peak_mmr ?? mmr}</div></div>
        <div className="rounded-lg bg-black/40 p-2"><div className="text-[10px] text-zinc-500">Games</div><div className="font-bold tabular-nums">{block?.games_played ?? 0}</div></div>
        <div className="rounded-lg bg-black/40 p-2"><div className="text-[10px] text-zinc-500">Wins</div><div className="font-bold tabular-nums">{block?.wins ?? 0}</div></div>
      </div>
      <TierProgress mmr={mmr} tier={tier} />
      <button
        data-testid={`ranked-queue-btn-${mode}`}
        onClick={() => onPlay(mode)}
        disabled={disabled || queued || !eligible}
        className="w-full px-5 py-3 rounded-xl bg-[var(--accent)] text-black font-black uppercase tracking-widest hover:brightness-110 disabled:opacity-30 transition"
      >
        {!eligible ? "Locked" : queued ? "Queued…" : `Find ${label} match`}
      </button>
    </div>
  );
}

function QueueModal({ snap, onCancel }) {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => clearInterval(id);
  }, []);
  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-40 bg-black/80 grid place-items-center px-6"
      data-testid="matchmaking-modal"
    >
      <motion.div
        initial={{ scale: 0.92, y: 12 }} animate={{ scale: 1, y: 0 }}
        className="w-full max-w-md rounded-2xl border border-white/10 bg-[var(--surface)] p-8 text-center space-y-5"
      >
        <Loader2 size={42} className="mx-auto animate-spin text-[var(--accent)]" />
        <div>
          <div className="text-xs uppercase tracking-[0.4em] text-[var(--accent)]">searching</div>
          <h2 className="text-2xl font-black mt-1">Finding ranked {snap.mode.replace("_", " ")} match…</h2>
          <div className="text-sm text-zinc-400 mt-2">
            Queue size <span className="font-bold text-white">{snap.queue_size}</span> · position #{snap.queue_position}
          </div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-2">{elapsed}s elapsed · widening MMR window</div>
        </div>
        <button
          data-testid="matchmaking-cancel-btn"
          onClick={onCancel}
          className="inline-flex items-center gap-2 px-5 py-2 rounded-full border border-white/15 hover:bg-white/5 text-xs uppercase tracking-widest"
        >
          <X size={14} /> Cancel queue
        </button>
      </motion.div>
    </motion.div>
  );
}

export default function Ranked() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [snap, setSnap] = useState(null);
  const [pendingMode, setPendingMode] = useState(null);
  const lastEventRef = useRef(null);

  const refresh = async () => {
    try {
      const [{ data: rme }, { data: q }] = await Promise.all([
        api.get("/ranked/me"),
        api.get("/ranked/queue/status"),
      ]);
      setMe(rme);
      setSnap(q.in_queue ? q : null);
    } catch (e) {
      toast.error("Could not load ranked status");
    }
  };

  useEffect(() => { refresh(); }, []);

  // Listen to ranked_match_found push → redirect into the lobby.
  useEffect(() => {
    const off = onPersonalEvent((evt) => {
      if (evt.type === "ranked_match_found") {
        lastEventRef.current = evt.payload;
        setSnap(null);
        setPendingMode(null);
        toast.success("Ranked match found!", {
          description: `${evt.payload.players.length} players${evt.payload.difficulty ? ` · ${evt.payload.difficulty}` : ""}`,
        });
        setTimeout(() => navigate(`/lobby/${evt.payload.lobby_code}`), 500);
      }
      if (evt.type === "ranked_queue_cancelled") setSnap(null);
    });
    return off;
  }, [navigate]);

  const onPlay = async (mode) => {
    setPendingMode(mode);
    try {
      const { data } = await api.post("/ranked/queue", { mode });
      setSnap(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not queue");
      setPendingMode(null);
    }
  };

  const onCancel = async () => {
    try { await api.post("/ranked/queue/cancel"); } catch (e) { /* ignore */ }
    setSnap(null);
    setPendingMode(null);
  };

  const eligible = !!me?.eligible;
  const left = me?.games_to_unlock ?? 10;
  const queued = !!snap;

  return (
    <div className="min-h-screen bg-[var(--bg)] text-[var(--text)]" data-testid="ranked-page">
      <header className="px-6 py-5 max-w-6xl mx-auto flex items-center justify-between">
        <Link to="/dashboard" className="text-xs uppercase tracking-[0.4em] text-[var(--accent)] hover:brightness-125">← dashboard</Link>
        <div className="text-xs uppercase tracking-[0.3em] text-zinc-400">ranked</div>
      </header>

      <main className="max-w-6xl mx-auto px-6 pb-16 space-y-8">
        <motion.section initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-center gap-3">
            <Swords size={28} className="text-[var(--accent)]" />
            <h1 className="text-4xl sm:text-5xl font-black">Ranked Mode</h1>
          </div>
          <p className="text-zinc-400 mt-2 max-w-2xl">
            Server-owned lobbies. Tier-aware difficulty for Classic &amp; Career Path; Quiz mixes all difficulties.
            FFA Elo. Climb 8 tiers from iron → grandmaster. Disconnect = penalty. Settings are locked.
          </p>
          {me?.season && (
            <div className="mt-4 inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-zinc-400">
              <Trophy size={12} /> {me.season.name} · ends {new Date(me.season.end_at).toLocaleDateString()}
            </div>
          )}
        </motion.section>

        {!eligible && (
          <div className="rounded-2xl border border-amber-400/30 bg-amber-400/5 p-5 flex items-center gap-3"
               data-testid="ranked-locked-banner">
            <ShieldCheck className="text-amber-300" size={22} />
            <div className="text-sm">
              <div className="font-bold">Ranked is locked.</div>
              <div className="text-zinc-400">Play <span className="text-amber-300 font-bold">{left} more casual games</span> to unlock ranked queues.</div>
            </div>
            <Link to="/lobby/new" className="ml-auto px-4 py-2 rounded-full bg-[var(--accent)] text-black font-bold text-xs uppercase tracking-widest">
              Play casual →
            </Link>
          </div>
        )}

        <section className="grid md:grid-cols-3 gap-4">
          <ModeCard mode="classic" label="Classic" sub="Teammate puzzles"
            block={me?.classic} onPlay={onPlay}
            disabled={pendingMode !== null && pendingMode !== "classic"}
            queued={snap?.mode === "classic"} eligible={eligible} />
          <ModeCard mode="career_path" label="Career Path" sub="Career timeline"
            block={me?.career_path} onPlay={onPlay}
            disabled={pendingMode !== null && pendingMode !== "career_path"}
            queued={snap?.mode === "career_path"} eligible={eligible} />
          <ModeCard mode="quiz" label="Quiz" sub="Trivia, no cards"
            block={me?.quiz} onPlay={onPlay}
            disabled={pendingMode !== null && pendingMode !== "quiz"}
            queued={snap?.mode === "quiz"} eligible={eligible} />
        </section>

        <section data-testid="ranked-leaderboards-cta"
                 className="rounded-2xl border border-white/10 bg-[var(--surface)] p-5 flex items-center gap-3">
          <TrendingUp className="text-[var(--accent)]" />
          <div className="text-sm">
            <div className="font-bold">Climbing the ladder?</div>
            <div className="text-zinc-400 text-xs">Check the Ranked leaderboards to see how you stack up this season.</div>
          </div>
          <Link to="/leaderboards" className="ml-auto px-4 py-2 rounded-full bg-white/5 border border-white/15 text-xs uppercase tracking-widest hover:bg-white/10">
            Open Leaderboards →
          </Link>
        </section>
      </main>

      <AnimatePresence>
        {queued && <QueueModal snap={snap} onCancel={onCancel} />}
      </AnimatePresence>

      {/* Merged-in /ranks explainer (also handles guest sign-up CTA). */}
      <main className="max-w-6xl mx-auto px-6 pb-16">
        <RanksExplainer />
      </main>
    </div>
  );
}
